import React, {useMemo} from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Box,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  FormLabel,
  RadioGroup,
  Radio,
  Button,
  IconButton,
  Paper,
} from '@mui/material';
import MDTypography from 'components/MDTypography';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {ChromePicker} from 'react-color';
import {Controller} from 'react-hook-form';
import {Add, Delete} from '@mui/icons-material';

import DeviceImageUploader from 'examples/DeviceType/DeviceImageUploader';
import DeviceGalleryUploader from 'examples/DeviceType/DeviceGalleryUploader';

// Formats the wysiwyg toolbar can actually produce — anything else arriving
// via copy/paste (from Word, Google Docs, another site, etc.) is stripped
// down to this set instead of carrying over stray inline styles/classes
// that would just get sanitized away anyway.
const ALLOWED_PASTE_FORMATS = [
  'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'indent', 'header', 'color', 'background', 'link',
];

const stripUnknownPasteFormats = (node, delta) => {
  delta.ops.forEach((op) => {
    if (!op.attributes) return;
    Object.keys(op.attributes).forEach((key) => {
      if (!ALLOWED_PASTE_FORMATS.includes(key)) delete op.attributes[key];
    });
  });
  return delta;
};

const wysiwygQuillModules = {
  toolbar: [
    [{header: [1, 2, 3, 4, false]}],
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote'],
    [{list: 'ordered'}, {list: 'bullet'}],
    [{indent: '-1'}, {indent: '+1'}],
    [{color: []}, {background: []}],
    ['link'],
    ['clean'],
  ],
  clipboard: {
    matchVisual: false,
    matchers: [[Node.ELEMENT_NODE, stripUnknownPasteFormats]],
  },
};

const ColorPicker = ({value, onChange, label, error, helperText}) => {
  const [showPicker, setShowPicker] = React.useState(false);

  return (
    <Box>
      <MDTypography
        variant="caption"
        color={error ? 'error' : 'text'}
        gutterBottom>
        {label}
      </MDTypography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          border: '1px solid',
          borderColor: error ? 'error.main' : 'grey.300',
          borderRadius: 1,
          cursor: 'pointer',
          bgcolor: error ? 'error.light' : 'transparent',
        }}
        onClick={() => setShowPicker(!showPicker)}>
        <Box
          sx={{
            width: 30,
            height: 30,
            bgcolor: value || '#ffffff',
            border: '1px solid',
            borderColor: 'grey.400',
            borderRadius: 1,
          }}
        />
        <MDTypography variant="body2">{value || '#ffffff'}</MDTypography>
      </Box>
      {showPicker && (
        <Box sx={{position: 'absolute', zIndex: 2, mt: 1}}>
          <Box
            sx={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0}}
            onClick={() => setShowPicker(false)}
          />
          <ChromePicker
            color={value || '#ffffff'}
            onChange={color => onChange(color.hex)}
          />
        </Box>
      )}
      {error && (
        <MDTypography
          variant="caption"
          color="error"
          sx={{mt: 0.5, display: 'block'}}>
          {helperText}
        </MDTypography>
      )}
    </Box>
  );
};

const DEPTH_COLORS = ['grey.50', 'blue.50', 'purple.50', 'green.50'];
const DEPTH_BORDER_COLORS = ['divider', 'info.light', 'secondary.light', 'success.light'];

const FieldRenderer = ({
  field,
  control,
  register,
  watch,
  setValue,
  errors,
  path = '',
  namePrefix = 'defaultStructure',
  imageFiles = {},
  onImageFileChange,
  galleryFiles = {},
  onGalleryFilesChange,
  depth = 0,
}) => {
  const fieldPath = path
    ? `${namePrefix}.${path}.${field.key}`
    : `${namePrefix}.${field.key}`;
  const fieldPathKey = useMemo(() => {
    const raw = path ? `${path}.${field.key}` : field.key;
    return raw.replace(/\./g, '_');
  }, [path, field.key]);

  const getNestedError = (errObj, dotPath) => {
    return dotPath
      .split('.')
      .reduce((acc, key) => (acc ? acc[key] : undefined), errObj);
  };

  const errorPath = path ? `${path}.${field.key}` : field.key;
  const fieldError = getNestedError(errors?.[namePrefix], errorPath);

  const commonProps = {
    fullWidth: true,
    size: 'small',
    label: field.label,
    error: !!fieldError,
    helperText: fieldError?.message,
  };

  const currentValue = watch(fieldPath);

  const memoizedRepeaterValue = useMemo(
    () => (Array.isArray(currentValue) ? currentValue : []),
    [currentValue],
  );

  const memoizedGalleryValue = useMemo(
    () => (Array.isArray(currentValue) ? currentValue : []),
    [currentValue],
  );

  const memoizedResponsiveImageValue = useMemo(() => {
    if (!currentValue) return {};
    if (typeof currentValue === 'string') return {desktop: currentValue};
    return currentValue;
  }, [currentValue]);

  const memoizedSimpleImageValue = useMemo(() => {
    if (!currentValue) return {};
    if (typeof currentValue === 'string') return {desktop: currentValue};
    return currentValue;
  }, [currentValue]);

  switch (field.type) {
    case 'text':
      return (
        <TextField
          {...commonProps}
          placeholder={field.placeholder || `Enter ${field.label}`}
          {...register(fieldPath, {
            required: field.required ? `${field.label} is required` : false,
          })}
        />
      );

    case 'textarea':
      return (
        <TextField
          {...commonProps}
          multiline
          rows={4}
          placeholder={field.placeholder || `Enter ${field.label}`}
          {...register(fieldPath, {
            required: field.required ? `${field.label} is required` : false,
          })}
        />
      );

    case 'wysiwyg':
      return (
        <Box>
          <MDTypography variant="caption" color="text" gutterBottom>
            {field.label}{' '}
            {field.required && <span style={{color: 'red'}}>*</span>}
          </MDTypography>
          <Controller
            name={fieldPath}
            control={control}
            rules={{
              required: field.required ? `${field.label} is required` : false,
            }}
            render={({field: {onChange, value}}) => (
              <ReactQuill
                theme="snow"
                value={value || ''}
                onChange={onChange}
                modules={wysiwygQuillModules}
                style={{height: 320, marginBottom: 52}}
              />
            )}
          />
          {fieldError && (
            <MDTypography variant="caption" color="error">
              {fieldError.message}
            </MDTypography>
          )}
        </Box>
      );

    case 'number':
      return (
        <TextField
          {...commonProps}
          type="number"
          placeholder={field.placeholder}
          {...register(fieldPath, {
            required: field.required ? `${field.label} is required` : false,
            valueAsNumber: true,
          })}
        />
      );

    case 'email':
      return (
        <TextField
          {...commonProps}
          type="email"
          placeholder={field.placeholder}
          {...register(fieldPath, {
            required: field.required ? `${field.label} is required` : false,
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address',
            },
          })}
        />
      );

    case 'date':
      return (
        <TextField
          {...commonProps}
          type="date"
          {...register(fieldPath, {
            required: field.required ? `${field.label} is required` : false,
          })}
          InputLabelProps={{shrink: true}}
        />
      );

    case 'icon':
      return (
        <TextField
          {...commonProps}
          placeholder="e.g., FaHome, MdSettings"
          {...register(fieldPath, {
            required: field.required ? `${field.label} is required` : false,
          })}
        />
      );

    case 'checkbox':
      return (
        <FormControlLabel
          control={
            <Controller
              name={fieldPath}
              control={control}
              render={({field: {onChange, value}}) => (
                <Checkbox
                  checked={value || false}
                  onChange={e => onChange(e.target.checked)}
                />
              )}
            />
          }
          label={field.label}
        />
      );

    case 'select':
      return (
        <FormControl fullWidth size="small" error={!!fieldError}>
          <InputLabel>{field.label}</InputLabel>
          <Controller
            name={fieldPath}
            control={control}
            rules={{
              required: field.required ? `${field.label} is required` : false,
            }}
            render={({field: {onChange, value}}) => (
              <Select
                label={field.label}
                value={value || ''}
                onChange={onChange}>
                {field.options?.map((opt, idx) => (
                  <MenuItem key={idx} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {fieldError && <FormHelperText>{fieldError.message}</FormHelperText>}
        </FormControl>
      );

    case 'radio':
      return (
        <FormControl component="fieldset" error={!!fieldError}>
          <FormLabel component="legend">{field.label}</FormLabel>
          <Controller
            name={fieldPath}
            control={control}
            rules={{
              required: field.required ? `${field.label} is required` : false,
            }}
            render={({field: {onChange, value}}) => (
              <RadioGroup
                value={value || ''}
                onChange={e => onChange(e.target.value)}>
                {field.options?.map((opt, idx) => (
                  <FormControlLabel
                    key={idx}
                    value={opt.value}
                    control={<Radio />}
                    label={opt.label}
                  />
                ))}
              </RadioGroup>
            )}
          />
          {fieldError && <FormHelperText>{fieldError.message}</FormHelperText>}
        </FormControl>
      );

    case 'color':
      return (
        <Controller
          name={fieldPath}
          control={control}
          render={({field: {onChange, value}}) => (
            <ColorPicker
              value={value}
              onChange={onChange}
              label={field.label}
              error={!!fieldError}
              helperText={fieldError?.message}
            />
          )}
        />
      );

    case 'link':
      return (
        <Box>
          <MDTypography variant="caption" color="text" gutterBottom>
            {field.label}
          </MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Text"
                {...register(`${fieldPath}.text`)}
                placeholder="Button text"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="URL"
                {...register(`${fieldPath}.url`)}
                placeholder="https://..."
              />
            </Grid>
          </Grid>
        </Box>
      );

    case 'image':
      return (
        <Controller
          name={fieldPath}
          control={control}
          rules={{
            required: field.required ? `${field.label} is required` : false,
          }}
          render={({field: {onChange, value}}) => (
            <DeviceImageUploader
              value={typeof value === 'object' ? value : {desktop: value || ''}}
              onChange={onChange}
              label={field.label}
              required={field.required}
              files={imageFiles[fieldPathKey] || {}}
              onFileChange={(device, file) => {
                if (onImageFileChange) {
                  onImageFileChange(fieldPathKey, device, file);
                }
              }}
              error={!!fieldError}
              helperText={fieldError?.message}
              deviceTypes={['mobile', 'tablet', 'desktop']}
              showCopyToAll={true}
            />
          )}
        />
      );

    case 'gallery':
      return (
        <Controller
          name={fieldPath}
          control={control}
          defaultValue={[]}
          rules={{
            required: field.required ? `${field.label} is required` : false,
          }}
          render={({field: {onChange, value}}) => {
            // console.log("value : ", value);
            // const safeValue = Array.isArray(value) ? value : [];
            const safeValue = Array.isArray(value)
              ? value.filter(
                  item =>
                    item &&
                    typeof item === 'object' &&
                    Object.values(item).some(v => v && v !== ''),
                )
              : [];

            return (
              <DeviceGalleryUploader
                value={safeValue}
                onChange={onChange}
                label={field.label}
                required={field.required}
                files={galleryFiles[fieldPathKey] || {}}
                onFilesChange={newFilesMap => {
                  if (onGalleryFilesChange) {
                    onGalleryFilesChange(fieldPathKey, newFilesMap);
                  }
                }}
                error={!!fieldError}
                helperText={fieldError?.message}
                deviceTypes={['mobile', 'tablet', 'desktop']}
              />
            );
          }}
        />
      );

    case 'repeater': {
      const repeaterBg = depth === 0 ? 'grey.50' : depth === 1 ? '#f0f7ff' : depth === 2 ? '#f5f0ff' : '#f0fff4';
      const repeaterBorderColor = depth === 0 ? 'grey.300' : depth === 1 ? 'info.light' : depth === 2 ? 'secondary.light' : 'success.light';

      return (
        <Box
          sx={{
            mt: 1.5,
            p: {xs: 1, sm: 1.5, md: 2},
            bgcolor: repeaterBg,
            borderRadius: 1,
            border: '1px solid',
            borderColor: repeaterBorderColor,
            width: '100%',
            boxSizing: 'border-box',
          }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            mb={1.5}>
            <MDTypography variant="body2" color="info" gutterBottom sx={{mb: 0}}>
              {field.label} — Repeatable Items
            </MDTypography>
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => {
                const curr = watch(fieldPath);
                const currentArr = Array.isArray(curr) ? curr : [];
                setValue(fieldPath, [...currentArr, {}]);
              }}>
              Add Item
            </Button>
          </Box>

          <Controller
            name={fieldPath}
            control={control}
            defaultValue={[]}
            render={({field: {onChange}}) => (
              <Box>
                {memoizedRepeaterValue.length === 0 ? (
                  <Paper sx={{p: 2, textAlign: 'center', bgcolor: 'grey.100'}}>
                    <MDTypography variant="caption" color="textSecondary">
                      No items yet. Click "Add Item" to add content.
                    </MDTypography>
                  </Paper>
                ) : (
                  memoizedRepeaterValue.map((_, index) => {
                    const itemPath = path
                      ? `${path}.${field.key}.${index}`
                      : `${field.key}.${index}`;

                    return (
                      <Paper
                        key={index}
                        sx={{
                          p: {xs: 1, sm: 1.5, md: 2},
                          mb: 1.5,
                          position: 'relative',
                          overflow: 'hidden',
                        }}>
                        <Box sx={{pr: {xs: 4, sm: 5}}}>
                          <MDTypography
                            variant="caption"
                            color="textSecondary"
                            sx={{display: 'block', mb: 1}}>
                            Item {index + 1}
                          </MDTypography>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          sx={{position: 'absolute', top: 8, right: 8}}
                          onClick={() => {
                            const newValue = [...memoizedRepeaterValue];
                            newValue.splice(index, 1);
                            onChange(newValue);
                          }}>
                          <Delete fontSize="small" />
                        </IconButton>

                        <Grid container spacing={{xs: 1, sm: 1.5, md: 2}}>
                          {field.fields?.map(subField => {
                            const isWide = ['repeater', 'group', 'richtext', 'gallery'].includes(subField.type);
                            return (
                              <Grid
                                item
                                xs={12}
                                sm={isWide ? 12 : 6}
                                md={isWide ? 12 : 6}
                                key={subField.key}
                                sx={{minWidth: 0}}>
                                <FieldRenderer
                                  field={subField}
                                  control={control}
                                  register={register}
                                  watch={watch}
                                  setValue={setValue}
                                  errors={errors}
                                  path={itemPath}
                                  namePrefix={namePrefix}
                                  imageFiles={imageFiles}
                                  onImageFileChange={onImageFileChange}
                                  galleryFiles={galleryFiles}
                                  onGalleryFilesChange={onGalleryFilesChange}
                                  depth={depth + 1}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Paper>
                    );
                  })
                )}
              </Box>
            )}
          />
        </Box>
      );
    }

    case 'group': {
      const groupBg = depth === 0 ? 'grey.50' : depth === 1 ? '#f0f7ff' : depth === 2 ? '#f5f0ff' : '#f0fff4';
      const groupBorderColor = depth === 0 ? 'grey.300' : depth === 1 ? 'info.light' : depth === 2 ? 'secondary.light' : 'success.light';

      return (
        <Box
          sx={{
            mt: 1.5,
            p: {xs: 1, sm: 1.5, md: 2},
            bgcolor: groupBg,
            borderRadius: 1,
            border: '1px solid',
            borderColor: groupBorderColor,
            width: '100%',
            boxSizing: 'border-box',
          }}>
          <MDTypography variant="body2" color="info" gutterBottom>
            {field.label} — Group
          </MDTypography>

          {!field.fields || field.fields.length === 0 ? (
            <Paper sx={{p: 2, textAlign: 'center', bgcolor: 'grey.100'}}>
              <MDTypography variant="caption" color="textSecondary">
                No fields in this group. Define fields in the template builder.
              </MDTypography>
            </Paper>
          ) : (
            <Grid container spacing={{xs: 1, sm: 1.5, md: 2}}>
              {field.fields.map(subField => {
                const groupPath = path ? `${path}.${field.key}` : field.key;
                const isWide = ['repeater', 'group', 'richtext', 'gallery'].includes(subField.type);

                return (
                  <Grid item xs={12} sm={isWide ? 12 : 6} md={isWide ? 12 : 6} key={subField.key} sx={{minWidth: 0}}>
                    <FieldRenderer
                      field={subField}
                      control={control}
                      register={register}
                      watch={watch}
                      setValue={setValue}
                      errors={errors}
                      path={groupPath}
                      namePrefix={namePrefix}
                      imageFiles={imageFiles}
                      onImageFileChange={onImageFileChange}
                      galleryFiles={galleryFiles}
                      onGalleryFilesChange={onGalleryFilesChange}
                      depth={depth + 1}
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      );
    }

    default:
      return (
        <TextField
          {...commonProps}
          label={field.label || 'Field'}
          {...register(fieldPath, {
            required: field.required ? `${field.label} is required` : false,
          })}
        />
      );
  }
};

export default FieldRenderer;
